// ROI Calculator Data Constants
// Data sourced from production analytics - Last updated: 2025-01-27

import type {
  Industry,
  Provider,
  ProductType,
  CountryBenchmark,
  ProductBenchmark,
  ProductCategory,
  CountryProductBenchmark,
  PassRateInfo,
} from './types'

// Metadata
export const ROI_METADATA = {
  lastUpdated: '2025-01-27',
  dataSource: 'Prod_Ext_Customer_Latest_Entity_View_v1',
  dateFilter: 'Since July 2025',
}

// Industries with ARPU (Annual Revenue Per User)
export const INDUSTRIES: Industry[] = [
  { id: 'crypto', name: 'Crypto', arpu: { min: 50, max: 2500, monthly: 106.25 } },
  { id: 'banking', name: 'Banking', arpu: { min: 500, max: 3000, monthly: 145.83 } },
  { id: 'fintech', name: 'FinTech', arpu: { min: 50, max: 2000, monthly: 85.42 } },
  { id: 'ecommerce', name: 'Ecommerce', arpu: { min: 100, max: 500, monthly: 25.0 } },
  { id: 'igaming', name: 'iGaming', arpu: { min: 500, max: 2000, monthly: 104.17 } },
  { id: 'government', name: 'Government', arpu: { min: 0, max: 0, monthly: 0 } },
  { id: 'lending', name: 'Lending', arpu: { min: 500, max: 3000, monthly: 145.83 } },
  { id: 'bnpl', name: 'Buy Now Pay Later', arpu: { min: 50, max: 200, monthly: 10.42 } },
  { id: 'other', name: 'Other', arpu: { min: 0, max: 0, monthly: 0 } },
]

// Competitor providers
export const PROVIDERS: Provider[] = [
  { id: 'gbg', name: 'GBG', description: 'Global identity verification' },
  { id: 'greenid', name: 'Green ID', description: 'Australian identity verification' },
  { id: 'sumsub', name: 'Sumsub', description: 'Global KYC/AML platform' },
  { id: 'onfido', name: 'Onfido', description: 'AI-powered identity verification' },
  { id: 'jumio', name: 'Jumio', description: 'Identity verification & eKYC' },
  { id: 'trulioo', name: 'Trulioo', description: 'Global identity verification' },
  { id: 'idnow', name: 'IDnow', description: 'Identity verification platform' },
  { id: 'veriff', name: 'Veriff', description: 'Online identity verification' },
  { id: 'shufti', name: 'Shufti Pro', description: 'AI identity verification' },
  { id: 'persona', name: 'Persona', description: 'Identity infrastructure' },
  { id: 'socure', name: 'Socure', description: 'Digital identity verification' },
  { id: 'mitek', name: 'Mitek', description: 'Mobile identity solutions' },
  { id: 'idenfy', name: 'iDenfy', description: 'Identity verification services' },
  { id: 'idology', name: 'IDology', description: 'Identity verification & fraud' },
  { id: 'experian', name: 'Experian', description: 'Credit & identity services' },
  { id: 'equifax', name: 'Equifax', description: 'Credit & identity services' },
  { id: 'lexisnexis', name: 'LexisNexis', description: 'Risk & identity solutions' },
  { id: 'incode', name: 'Incode', description: 'Identity verification' },
  { id: 'au10tix', name: 'AU10TIX', description: 'Identity verification' },
  { id: 'inbuilding', name: 'In-house / Custom', description: 'Custom built solution' },
  { id: 'other', name: 'Other', description: 'Other provider' },
]

// Product types (current setup)
export const PRODUCT_TYPES: ProductType[] = [
  { id: 'kyc_basic', name: 'Basic KYC', description: 'Name, DOB, Address verification', category: 'KYC' },
  { id: 'kyc_standard', name: 'Standard KYC', description: 'KYC with document verification', category: 'KYC' },
  { id: 'kyc_enhanced', name: 'Enhanced KYC', description: 'Full KYC with biometrics', category: 'KYC' },
  { id: 'doc_verification', name: 'Document Verification', description: 'ID document scanning & verification', category: 'KYC' },
  { id: 'biometrics', name: 'Biometric Verification', description: 'Face match / liveness detection', category: 'KYC' },
  { id: 'kyb', name: 'Business Verification (KYB)', description: 'Company & director checks', category: 'KYB' },
  { id: 'aml_screening', name: 'AML Screening', description: 'Watchlist & sanctions screening', category: 'AML' },
  { id: 'pep_screening', name: 'PEP Screening', description: 'Politically exposed persons check', category: 'PEP' },
  { id: 'fraud_detection', name: 'Fraud Detection', description: 'Device & behavioral fraud checks', category: 'Fraud' },
]

// Country benchmarks
export const COUNTRY_BENCHMARKS: CountryBenchmark[] = [
  { country: 'Australia', totalVerifications: 2403334, passCount: 1588590, passRate: 66.1, customerCount: 258 },
  { country: 'United States', totalVerifications: 237372, passCount: 154794, passRate: 65.2, customerCount: 64 },
  { country: 'United Kingdom', totalVerifications: 232157, passCount: 177321, passRate: 76.4, customerCount: 70 },
  { country: 'Canada', totalVerifications: 191433, passCount: 83247, passRate: 43.5, customerCount: 42 },
  { country: 'New Zealand', totalVerifications: 173865, passCount: 109468, passRate: 63.0, customerCount: 90 },
  { country: 'France', totalVerifications: 152276, passCount: 80579, passRate: 52.9, customerCount: 32 },
  { country: 'Germany', totalVerifications: 142650, passCount: 108057, passRate: 75.8, customerCount: 35 },
  { country: 'Brazil', totalVerifications: 119464, passCount: 23545, passRate: 19.7, customerCount: 20 },
  { country: 'Spain', totalVerifications: 92613, passCount: 43470, passRate: 46.9, customerCount: 27 },
  { country: 'Italy', totalVerifications: 64450, passCount: 40964, passRate: 63.6, customerCount: 29 },
  { country: 'Netherlands', totalVerifications: 60523, passCount: 27436, passRate: 45.3, customerCount: 29 },
  { country: 'Poland', totalVerifications: 40059, passCount: 12625, passRate: 31.5, customerCount: 17 },
  { country: 'Belgium', totalVerifications: 27278, passCount: 10728, passRate: 39.3, customerCount: 21 },
  { country: 'Switzerland', totalVerifications: 24793, passCount: 14052, passRate: 56.7, customerCount: 31 },
  { country: 'Colombia', totalVerifications: 20149, passCount: 16674, passRate: 82.8, customerCount: 15 },
  { country: 'Sweden', totalVerifications: 12836, passCount: 9068, passRate: 70.6, customerCount: 21 },
  { country: 'Austria', totalVerifications: 12103, passCount: 4456, passRate: 36.8, customerCount: 21 },
  { country: 'Singapore', totalVerifications: 8498, passCount: 4430, passRate: 52.1, customerCount: 56 },
  { country: 'Norway', totalVerifications: 7444, passCount: 3952, passRate: 53.1, customerCount: 14 },
  { country: 'Ireland', totalVerifications: 6842, passCount: 4521, passRate: 66.1, customerCount: 24 },
  { country: 'Portugal', totalVerifications: 6234, passCount: 3187, passRate: 51.1, customerCount: 18 },
  { country: 'Denmark', totalVerifications: 5891, passCount: 3498, passRate: 59.4, customerCount: 15 },
  { country: 'Finland', totalVerifications: 4523, passCount: 2876, passRate: 63.6, customerCount: 12 },
  { country: 'Mexico', totalVerifications: 4201, passCount: 2845, passRate: 67.7, customerCount: 11 },
  { country: 'Japan', totalVerifications: 3892, passCount: 2134, passRate: 54.8, customerCount: 8 },
  { country: 'Hong Kong', totalVerifications: 3456, passCount: 1923, passRate: 55.6, customerCount: 19 },
  { country: 'South Africa', totalVerifications: 2987, passCount: 1654, passRate: 55.4, customerCount: 7 },
  { country: 'India', totalVerifications: 2543, passCount: 1876, passRate: 73.8, customerCount: 12 },
  { country: 'Philippines', totalVerifications: 2134, passCount: 1543, passRate: 72.3, customerCount: 9 },
  { country: 'Indonesia', totalVerifications: 1876, passCount: 1234, passRate: 65.8, customerCount: 6 },
]

// FrankieOne Product benchmarks
export const PRODUCT_BENCHMARKS: ProductBenchmark[] = [
  // KYC Products - Data Sources
  { profile: 'au-all-sources', totalVerifications: 25678, passCount: 18234, passRate: 71.0, customerCount: 5, description: 'Australia All Sources', category: 'KYC', incrementalLift: 15.0 },
  { profile: 'nz-all-sources', totalVerifications: 116058, passCount: 72548, passRate: 62.5, customerCount: 2, description: 'New Zealand All Sources', category: 'KYC', incrementalLift: 12.0 },
  { profile: 'international', totalVerifications: 1417486, passCount: 752306, passRate: 53.1, customerCount: 33, description: 'International Identity Verification', category: 'KYC', incrementalLift: 8.0 },
  { profile: 'default', totalVerifications: 614816, passCount: 530318, passRate: 86.3, customerCount: 79, description: 'Standard KYC', category: 'KYC', incrementalLift: 18.0 },

  // Safe Harbour Products
  { profile: 'safe_harbour_id', totalVerifications: 229642, passCount: 159930, passRate: 69.6, customerCount: 131, description: 'Safe Harbour with ID', category: 'KYC', incrementalLift: 12.0 },
  { profile: 'safe_harbour', totalVerifications: 95210, passCount: 82179, passRate: 86.3, customerCount: 77, description: 'Safe Harbour', category: 'KYC', incrementalLift: 16.0 },
  { profile: 'safe_harbour_adult', totalVerifications: 77387, passCount: 56488, passRate: 73.0, customerCount: 1, description: 'Safe Harbour (Adult)', category: 'KYC', incrementalLift: 10.0 },
  { profile: 'safe_harbour_dob', totalVerifications: 31458, passCount: 25307, passRate: 80.5, customerCount: 3, description: 'Safe Harbour with DOB', category: 'KYC', incrementalLift: 14.0 },
  { profile: 'safe_harbour_id_cra', totalVerifications: 30333, passCount: 27989, passRate: 92.3, customerCount: 2, description: 'Safe Harbour ID + Credit', category: 'KYC', incrementalLift: 20.0 },

  // Government ID Products
  { profile: 'gov_id', totalVerifications: 160156, passCount: 125816, passRate: 78.5, customerCount: 72, description: 'Government ID Verification', category: 'KYC', incrementalLift: 14.0 },
  { profile: 'govid_only', totalVerifications: 94766, passCount: 29457, passRate: 31.1, customerCount: 1, description: 'Government ID Only', category: 'KYC', incrementalLift: 5.0 },
  { profile: 'gov_id_dl', totalVerifications: 48340, passCount: 34085, passRate: 70.5, customerCount: 1, description: 'Drivers License Verification', category: 'KYC', incrementalLift: 12.0 },

  // Biometrics
  { profile: 'dvs_bio', totalVerifications: 61608, passCount: 53836, passRate: 87.4, customerCount: 1, description: 'DVS with Biometrics', category: 'Biometrics', incrementalLift: 18.0 },
  { profile: 'biometrics_me', totalVerifications: 28456, passCount: 22498, passRate: 79.1, customerCount: 1, description: 'Biometrics (Middle East)', category: 'Biometrics', incrementalLift: 14.0 },

  // Standard Products
  { profile: 'idv_only', totalVerifications: 32951, passCount: 28986, passRate: 88.0, customerCount: 7, description: 'Identity Verification Only', category: 'KYC', incrementalLift: 16.0 },
  { profile: 'no_biometrics', totalVerifications: 39379, passCount: 33582, passRate: 85.3, customerCount: 1, description: 'Verification without Biometrics', category: 'KYC', incrementalLift: 15.0 },
  { profile: 'standard', totalVerifications: 31431, passCount: 20684, passRate: 65.8, customerCount: 16, description: 'Standard Verification', category: 'KYC', incrementalLift: 10.0 },
  { profile: 'standard_nogovid', totalVerifications: 50550, passCount: 47036, passRate: 93.1, customerCount: 1, description: 'Standard (No Gov ID)', category: 'KYC', incrementalLift: 20.0 },
  { profile: 'standard_kyc_fraud', totalVerifications: 36480, passCount: 26063, passRate: 71.4, customerCount: 1, description: 'Standard KYC + Fraud', category: 'KYC', incrementalLift: 12.0 },
  { profile: 'credit_score', totalVerifications: 37061, passCount: 29658, passRate: 80.0, customerCount: 1, description: 'Credit Score Check', category: 'KYC', incrementalLift: 14.0 },

  // KYB Products
  { profile: 'organisation', totalVerifications: 45610, passCount: 26030, passRate: 57.1, customerCount: 102, description: 'Organisation Verification', category: 'KYB', incrementalLift: 10.0 },
  { profile: 'sole_trader', totalVerifications: 36537, passCount: 24571, passRate: 67.3, customerCount: 1, description: 'Sole Trader Verification', category: 'KYB', incrementalLift: 12.0 },

  // AML Products
  { profile: 'aml_only', totalVerifications: 139774, passCount: 91654, passRate: 65.6, customerCount: 33, description: 'AML Screening Only', category: 'AML', incrementalLift: 8.0 },

  // PEP Products
  { profile: 'pep_sanctions', totalVerifications: 45000, passCount: 42750, passRate: 95.0, customerCount: 28, description: 'PEP & Sanctions Screening', category: 'PEP', incrementalLift: 5.0 },
]

// Product categories for grouped display
export const PRODUCT_CATEGORIES: ProductCategory[] = [
  { id: 'KYC', name: 'KYC - Identity Verification', description: 'Identity verification products', icon: 'user-check' },
  { id: 'Biometrics', name: 'Biometrics', description: 'Biometric verification products', icon: 'scan' },
  { id: 'KYB', name: 'KYB - Business Verification', description: 'Business verification products', icon: 'building' },
  { id: 'AML', name: 'AML Screening', description: 'Anti-money laundering screening', icon: 'shield' },
  { id: 'PEP', name: 'PEP & Sanctions', description: 'PEP and sanctions screening', icon: 'alert-triangle' },
]

// Country + Product combinations
export const COUNTRY_PRODUCT_BENCHMARKS: CountryProductBenchmark[] = [
  { country: 'Australia', profile: 'international', passRate: 42.3, totalVerifications: 156789, passCount: 66322 },
  { country: 'Australia', profile: 'default', passRate: 94.9, totalVerifications: 376890, passCount: 357504 },
  { country: 'Australia', profile: 'safe_harbour_id', passRate: 78.2, totalVerifications: 145678, passCount: 113919 },
  { country: 'Australia', profile: 'gov_id', passRate: 82.5, totalVerifications: 89234, passCount: 73618 },
  { country: 'Australia', profile: 'dvs_bio', passRate: 87.4, totalVerifications: 61608, passCount: 53836 },
  { country: 'United States', profile: 'international', passRate: 58.4, totalVerifications: 98765, passCount: 57678 },
  { country: 'United States', profile: 'standard', passRate: 72.1, totalVerifications: 45678, passCount: 32934 },
  { country: 'United Kingdom', profile: 'international', passRate: 68.5, totalVerifications: 123456, passCount: 84567 },
  { country: 'United Kingdom', profile: 'safe_harbour', passRate: 89.2, totalVerifications: 34567, passCount: 30833 },
  { country: 'New Zealand', profile: 'nz-all-sources', passRate: 62.5, totalVerifications: 116054, passCount: 72548 },
  { country: 'New Zealand', profile: 'safe_harbour_id', passRate: 74.8, totalVerifications: 23456, passCount: 17545 },
  { country: 'Canada', profile: 'standard_kyc_fraud', passRate: 71.4, totalVerifications: 36480, passCount: 26063 },
  { country: 'Canada', profile: 'international', passRate: 5.6, totalVerifications: 99153, passCount: 5557 },
  { country: 'Germany', profile: 'international', passRate: 72.8, totalVerifications: 78654, passCount: 57260 },
  { country: 'France', profile: 'international', passRate: 48.2, totalVerifications: 87654, passCount: 42249 },
  { country: 'Brazil', profile: 'international', passRate: 15.3, totalVerifications: 98765, passCount: 15111 },
  { country: 'Italy', profile: 'international', passRate: 63.7, totalVerifications: 45678, passCount: 29097 },
  { country: 'Spain', profile: 'international', passRate: 42.5, totalVerifications: 67890, passCount: 28853 },
  { country: 'Netherlands', profile: 'international', passRate: 41.2, totalVerifications: 45678, passCount: 18819 },
  { country: 'Singapore', profile: 'safe_harbour_id', passRate: 68.9, totalVerifications: 4567, passCount: 3147 },
]

// Utility functions

// Get pass rate for a specific country/product combination
export function getPassRate(country: string, productProfile: string): PassRateInfo | null {
  // First try to find exact combination
  const combination = COUNTRY_PRODUCT_BENCHMARKS.find(
    (item) => item.country === country && item.profile === productProfile
  )

  if (combination) {
    return {
      passRate: combination.passRate,
      sampleSize: combination.totalVerifications,
      confidence:
        combination.totalVerifications > 10000
          ? 'high'
          : combination.totalVerifications > 1000
            ? 'medium'
            : 'low',
    }
  }

  // If no exact match, use product benchmark
  const productData = PRODUCT_BENCHMARKS.find((p) => p.profile === productProfile)

  if (productData) {
    return {
      passRate: productData.passRate,
      sampleSize: productData.totalVerifications,
      confidence: 'product-benchmark',
    }
  }

  return null
}

// Get product by profile ID
export function getProduct(profileId: string): ProductBenchmark | undefined {
  return PRODUCT_BENCHMARKS.find((p) => p.profile === profileId)
}

// Get industry by ID
export function getIndustry(industryId: string): Industry | undefined {
  return INDUSTRIES.find((i) => i.id === industryId)
}

// Get provider by ID
export function getProvider(providerId: string): Provider | undefined {
  return PROVIDERS.find((p) => p.id === providerId)
}

// Get products grouped by category
export function getProductsByCategory(): Record<string, ProductBenchmark[]> {
  return PRODUCT_BENCHMARKS.reduce(
    (acc, product) => {
      if (!acc[product.category]) {
        acc[product.category] = []
      }
      acc[product.category].push(product)
      return acc
    },
    {} as Record<string, ProductBenchmark[]>
  )
}

// Format large numbers
export function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M'
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(0) + 'K'
  }
  return num.toLocaleString()
}

// Format currency
export function formatCurrency(num: number): string {
  if (num >= 1000000) {
    return '$' + (num / 1000000).toFixed(2) + 'M'
  }
  if (num >= 1000) {
    return '$' + (num / 1000).toFixed(1) + 'K'
  }
  return '$' + num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

// Get pass rate class for styling
export function getPassRateClass(rate: number): 'high' | 'medium' | 'low' {
  if (rate >= 70) return 'high'
  if (rate >= 50) return 'medium'
  return 'low'
}
